/**
 * Referral Program Service
 * Handles all referral-related API calls for agents and admin panel
 */
import api from "./api";

// ================== Types ==================

export interface ReferralStats {
  totalClicks: number;
  totalSignups: number;
  totalConversions: number;
  totalEarnings: number;
  pendingEarnings: number;
  conversionRate: number;
}

export interface StripeConnectStatus {
  connected: boolean;
  payoutsEnabled: boolean;
  onboardingComplete: boolean;
  accountId?: string;
  email?: string;
  country?: string;
  defaultCurrency?: string;
}

export interface ReferralOverview {
  code: string;
  customSlug: string | null;
  referralUrl: string;
  commissionPercent: number;
  isActive: boolean;
  stats: ReferralStats;
  stripeConnectStatus: StripeConnectStatus;
}

export type ReferralStatus = 'clicked' | 'signed_up' | 'trial' | 'converted' | 'churned';

export interface Referral {
  id: string;
  referralCodeId: string;
  referrerUserId: string;
  referredUserId?: string;
  referredUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    createdAt: string;
  };
  status: ReferralStatus;
  clickedAt?: string;
  signedUpAt?: string;
  trialStartedAt?: string;
  convertedAt?: string;
  churnedAt?: string;
  ipAddress?: string;
  userAgent?: string;
  landingPage?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  createdAt: string;
  updatedAt: string;
}

export type PayoutStatus = 'pending' | 'processing' | 'paid' | 'failed';

export interface ReferralCommission {
  id: string;
  referralId: string;
  referrerUserId: string;
  referredUserId: string;
  subscriptionId: string;
  subscriptionAmount: number;
  commissionPercent: number;
  commissionAmount: number;
  stripePaymentIntentId?: string;
  stripeInvoiceId?: string;
  stripeTransferId?: string;
  payoutStatus: PayoutStatus;
  paidAt?: string;
  periodStart?: string;
  periodEnd?: string;
  createdAt: string;
  updatedAt: string;
  // Joined data
  referredUser?: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface PayoutSummary {
  totalEarned: number;
  totalPaid: number;
  totalPending: number;
  pendingCount: number;
}

export interface AdminReferralStats {
  totalClicks: number;
  totalSignups: number;
  totalConversions: number;
  totalCommissionsGenerated: number;
  totalCommissionsPaid: number;
  totalCommissionsPending: number;
  activeReferrers: number;
  freeMonthsIssued?: number;
  freeMonthsApplied?: number;
  freeMonthsConsumed?: number;
  freeMonthsPending?: number;
}

export interface AdminReferrer {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  referralCode: string;
  customSlug?: string;
  totalClicks: number;
  totalSignups: number;
  totalConversions: number;
  freeMonthsEarned: number;
  freeMonthsApplied: number;
  freeMonthsConsumed: number;
  freeMonthsPending: number;
  isActive: boolean;
  createdAt: string;
}

export interface PendingCommission {
  id: string;
  referrerUserId: string;
  referrerName: string;
  referrerEmail: string;
  referredUserName: string;
  subscriptionAmount: number;
  commissionPercent: number;
  commissionAmount: number;
  stripeAccountId?: string;
  payoutsEnabled: boolean;
  createdAt: string;
}

// ================== API Response Types ==================

interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
  timestamp?: string;
  timeTakenMs?: number;
}

// ================== Endpoints ==================

export interface RewardStats {
  freeMonthsEarned: number;
  totalCreditCents: number;
}

export interface RewardsOverview extends ReferralOverview {
  rewards: RewardStats;
}

const REFERRAL_ENDPOINTS = {
  // User endpoints
  overview: '/api/referrals/overview',
  rewardsStatus: '/api/referrals/rewards-status',
  rewardsOverview: '/api/referrals/rewards-overview',
  customSlug: '/api/referrals/custom-slug',
  myReferrals: '/api/referrals/my-referrals',
  commissions: '/api/referrals/commissions',
  payoutSummary: '/api/referrals/payout-summary',
  stripeConnectStatus: '/api/referrals/stripe-connect/status',
  stripeConnectOnboard: '/api/referrals/stripe-connect/onboard',
  stripeConnectDashboard: '/api/referrals/stripe-connect/dashboard',

  // Public endpoints
  validateCode: (code: string) => `/api/public/referrals/validate/${code}`,
  trackClick: '/api/public/referrals/track-click',
  linkSignup: '/api/public/referrals/link-signup',

  // Admin endpoints
  adminStats: '/api/admin/referrals/stats',
  adminReferrers: '/api/admin/referrals/referrers',
  adminAllReferrals: '/api/admin/referrals/all',
  adminPendingCommissions: '/api/admin/referrals/pending-commissions',
  adminUpdateCommissionRate: '/api/admin/referrals/commission-rate',
  adminPayout: (id: string) => `/api/admin/referrals/payout/${id}`,
  adminBulkPayout: '/api/admin/referrals/payouts/bulk',
};

// ================== Service Functions ==================

/**
 * User Referral Functions
 */

/**
 * Get the current user's referral overview including code, stats, and Stripe Connect status
 */
export async function getReferralOverview(): Promise<ReferralOverview> {
  // Note: axios interceptor unwraps response.data, so response IS the ApiResponse body
  const response = await api.get<ApiResponse<ReferralOverview>>({
    url: REFERRAL_ENDPOINTS.overview,
  }) as unknown as ApiResponse<ReferralOverview>;
  return response.data;
}

/**
 * Check if the current user has joined the rewards program (without auto-creating a referral code)
 */
export async function getRewardsStatus(): Promise<{ hasJoined: boolean }> {
  const response = await api.get<ApiResponse<{ hasJoined: boolean }>>({
    url: REFERRAL_ENDPOINTS.rewardsStatus,
  }) as unknown as ApiResponse<{ hasJoined: boolean }>;
  return response.data;
}

/**
 * Get the current user's rewards overview (overview + reward stats)
 */
export async function getRewardsOverview(): Promise<RewardsOverview> {
  const response = await api.get<ApiResponse<RewardsOverview>>({
    url: REFERRAL_ENDPOINTS.rewardsOverview,
  }) as unknown as ApiResponse<RewardsOverview>;
  return response.data;
}

/**
 * Update the user's custom referral slug
 */
export async function updateCustomSlug(slug: string): Promise<{ customSlug: string; referralUrl: string }> {
  const response = await api.patch<ApiResponse<{ customSlug: string; referralUrl: string }>>({
    url: REFERRAL_ENDPOINTS.customSlug,
    body: { customSlug: slug },
  }) as unknown as ApiResponse<{ customSlug: string; referralUrl: string }>;
  return response.data;
}

/**
 * Get the user's referrals with pagination
 */
export async function getMyReferrals(params?: {
  status?: ReferralStatus;
  page?: number;
  limit?: number;
}): Promise<{ referrals: Referral[]; total: number; page: number; totalPages: number }> {
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.append('status', params.status);
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());

  const url = `${REFERRAL_ENDPOINTS.myReferrals}?${queryParams.toString()}`;
  const response = await api.get<ApiResponse<{ referrals: Referral[]; total: number; page: number; totalPages: number }>>({
    url,
  }) as any;
  return response.data;
}

/**
 * Get the user's commission history
 */
export async function getCommissions(params?: {
  status?: PayoutStatus;
  page?: number;
  limit?: number;
}): Promise<{ commissions: ReferralCommission[]; total: number; page: number; totalPages: number }> {
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.append('status', params.status);
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());

  const url = `${REFERRAL_ENDPOINTS.commissions}?${queryParams.toString()}`;
  const response = await api.get<ApiResponse<{ commissions: ReferralCommission[]; total: number; page: number; totalPages: number }>>({
    url,
  }) as any;
  return response.data;
}

/**
 * Get the user's payout summary
 */
export async function getPayoutSummary(): Promise<PayoutSummary> {
  const response = await api.get<ApiResponse<PayoutSummary>>({
    url: REFERRAL_ENDPOINTS.payoutSummary,
  }) as any;
  return response.data;
}

/**
 * Get the user's Stripe Connect account status
 */
export async function getStripeConnectStatus(): Promise<StripeConnectStatus> {
  const response = await api.get<ApiResponse<StripeConnectStatus>>({
    url: REFERRAL_ENDPOINTS.stripeConnectStatus,
  }) as any;
  return response.data;
}

/**
 * Start Stripe Connect onboarding flow
 * Returns a URL to redirect the user to complete Stripe onboarding
 */
export async function startStripeConnectOnboarding(): Promise<{ onboardingUrl: string; accountId: string }> {
  const response = await api.post<ApiResponse<{ onboardingUrl: string; accountId: string }>>({
    url: REFERRAL_ENDPOINTS.stripeConnectOnboard,
    body: {},
  }) as any;
  return response.data;
}

/**
 * Get a link to the Stripe Express dashboard for the user
 */
export async function getStripeConnectDashboardLink(): Promise<{ dashboardUrl: string }> {
  const response = await api.get<ApiResponse<{ dashboardUrl: string }>>({
    url: REFERRAL_ENDPOINTS.stripeConnectDashboard,
  }) as any;
  return response.data;
}

/**
 * Public Referral Functions (no auth required)
 */

/**
 * Validate a referral code or custom slug
 */
export async function validateReferralCode(code: string): Promise<{
  valid: boolean;
  referrerName?: string;
  code?: string;
}> {
  const response = await api.get<ApiResponse<{ valid: boolean; referrerName?: string; code?: string }>>({
    url: REFERRAL_ENDPOINTS.validateCode(code),
    auth: false,
  }) as any;
  return response.data;
}

/**
 * Track a referral link click
 */
export async function trackReferralClick(data: {
  code: string;
  ipAddress?: string;
  userAgent?: string;
  landingPage?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}): Promise<{ referralId: string }> {
  const response = await api.post<ApiResponse<{ referralId: string }>>({
    url: REFERRAL_ENDPOINTS.trackClick,
    body: data,
    auth: false,
  }) as any;
  return response.data;
}

/**
 * Link a new signup to a referral
 */
export async function linkSignupToReferral(data: {
  code: string;
  referredUserId: string;
}): Promise<{ linked: boolean }> {
  const response = await api.post<ApiResponse<{ linked: boolean }>>({
    url: REFERRAL_ENDPOINTS.linkSignup,
    body: data,
    auth: false,
  }) as any;
  return response.data;
}

/**
 * Admin Referral Functions
 */

/**
 * Get overall referral program stats (admin only)
 */
export async function getAdminReferralStats(): Promise<AdminReferralStats> {
  const response = await api.get<ApiResponse<AdminReferralStats>>({
    url: REFERRAL_ENDPOINTS.adminStats,
  }) as any;
  return response.data;
}

/**
 * Get all referrers (admin only)
 */
export async function getAdminReferrers(params?: {
  search?: string;
  sortBy?: 'totalEarnings' | 'totalConversions' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}): Promise<{ referrers: AdminReferrer[]; total: number; page: number; totalPages: number }> {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append('search', params.search);
  if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());

  const url = `${REFERRAL_ENDPOINTS.adminReferrers}?${queryParams.toString()}`;
  const response = await api.get<ApiResponse<{ referrers: AdminReferrer[]; total: number; page: number; totalPages: number }>>({
    url,
  }) as any;
  return response.data;
}

/**
 * Get all referrals (admin only)
 */
export async function getAdminAllReferrals(params?: {
  status?: ReferralStatus;
  referrerUserId?: string;
  page?: number;
  limit?: number;
}): Promise<{ referrals: Referral[]; total: number; page: number; totalPages: number }> {
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.append('status', params.status);
  if (params?.referrerUserId) queryParams.append('referrerUserId', params.referrerUserId);
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());

  const url = `${REFERRAL_ENDPOINTS.adminAllReferrals}?${queryParams.toString()}`;
  const response = await api.get<ApiResponse<{ referrals: Referral[]; total: number; page: number; totalPages: number }>>({
    url,
  }) as any;
  return response.data;
}

/**
 * Get pending commissions that need to be paid out (admin only)
 */
export async function getAdminPendingCommissions(): Promise<{ commissions: PendingCommission[]; total: number }> {
  const response = await api.get<ApiResponse<{ commissions: PendingCommission[]; total: number }>>({
    url: REFERRAL_ENDPOINTS.adminPendingCommissions,
  }) as any;
  return response.data;
}

/**
 * Update a user's commission rate (admin only)
 */
export async function updateUserCommissionRate(data: {
  userId: string;
  commissionPercent: number;
}): Promise<{ success: boolean }> {
  const response = await api.patch<ApiResponse<{ success: boolean }>>({
    url: REFERRAL_ENDPOINTS.adminUpdateCommissionRate,
    body: data,
  }) as any;
  return response.data;
}

/**
 * Toggle a referrer's active/inactive status (admin only)
 */
export async function toggleReferrerStatus(data: {
  userId: string;
  isActive: boolean;
}): Promise<{ success: boolean; isActive: boolean }> {
  const response = await api.patch<ApiResponse<{ success: boolean; isActive: boolean }>>({
    url: '/api/admin/referrals/toggle-status',
    body: data,
  }) as any;
  return response.data;
}

/**
 * Get detailed referrer info with their referrals and commissions (admin only)
 */
export async function getReferrerDetails(userId: string): Promise<{
  referralCode: any;
  user: any;
  referrals: Referral[];
  commissions: ReferralCommission[];
  stripeConnect: any;
}> {
  const response = await api.get<ApiResponse<any>>({
    url: `/api/admin/referrals/referrer/${userId}`,
  }) as any;
  return response.data;
}

/**
 * Process a single commission payout (admin only)
 */
export async function processCommissionPayout(commissionId: string): Promise<{
  success: boolean;
  transferId?: string;
  amount?: number;
}> {
  const response = await api.post<ApiResponse<{ success: boolean; transferId?: string; amount?: number }>>({
    url: REFERRAL_ENDPOINTS.adminPayout(commissionId),
    body: {},
  }) as any;
  return response.data;
}

/**
 * Process bulk commission payouts (admin only)
 */
export async function processBulkPayouts(data: {
  commissionIds?: string[];
  processAll?: boolean;
}): Promise<{
  processed: number;
  succeeded: number;
  failed: number;
  totalAmount: number;
  errors: Array<{ commissionId: string; error: string }>;
}> {
  const response = await api.post<ApiResponse<{
    processed: number;
    succeeded: number;
    failed: number;
    totalAmount: number;
    errors: Array<{ commissionId: string; error: string }>;
  }>>({
    url: REFERRAL_ENDPOINTS.adminBulkPayout,
    body: data,
  }) as any;
  return response.data;
}

// ================== Export Service Object ==================

const referralService = {
  // User functions
  getReferralOverview,
  getRewardsStatus,
  getRewardsOverview,
  updateCustomSlug,
  getMyReferrals,
  getCommissions,
  getPayoutSummary,
  getStripeConnectStatus,
  startStripeConnectOnboarding,
  getStripeConnectDashboardLink,

  // Public functions
  validateReferralCode,
  trackReferralClick,
  linkSignupToReferral,

  // Admin functions
  getAdminReferralStats,
  getAdminReferrers,
  getAdminAllReferrals,
  getAdminPendingCommissions,
  updateUserCommissionRate,
  toggleReferrerStatus,
  getReferrerDetails,
  processCommissionPayout,
  processBulkPayouts,
};

export default referralService;
